import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Building2,
  Phone,
  CheckCircle,
  XCircle,
  Search,
  User,
  Plane
} from 'lucide-react';
import { contractorService } from '../../services/contractorService';
import { AircraftContractor, CreateContractorRequest } from '../../types';
import { 
  validatePhoneNumber, 
  formatPhoneNumber, 
  getPhoneInputProps, 
  getPhoneValidationError 
} from '../../utils/phoneUtils';
import { toast } from 'react-hot-toast';

interface ContractorFormData {
  companyName: string;
  contactName: string;
  contactPhone: string;
  dispatchPhone: string;
  aircraftType: string;
  active: boolean;
}

const DEFAULT_CARRIERS = [
  'Contour Helicopters',
  'Airspan Helicopters',
  'Blackcomb Helicopters',
  'Black Tusk Helicopters',
  'Glacier Air (Squamish)',
  'Yellowhead Helicopters',
  'Bailey',
  '49 North',
  'Grand Prairie',
  'Arrow Helicopters (Revy)',
  'Glacier Helicopters (Revy)',
  'Other'
];

export default function ContractorManagement() {
  const [contractors, setContractors] = useState<AircraftContractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingContractor, setEditingContractor] = useState<AircraftContractor | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState<ContractorFormData>({
    companyName: '',
    contactName: '',
    contactPhone: '',
    dispatchPhone: '',
    aircraftType: '',
    active: true
  });
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  const aircraftTypes = [
    // Bell
    'Bell 206', 'Bell 212', 'Bell 407', 'Bell 412', 'Bell 429', 'Bell 505',
    // Airbus / Eurocopter
    'AS350', 'AS355', 'EC135', 'EC145',
    'Airbus H125', 'Airbus H130', 'Airbus H135', 'Airbus H145',
    // Leonardo / AgustaWestland
    'Leonardo AW109', 'Leonardo AW119', 'Leonardo AW139', 'Leonardo AW169',
    // Sikorsky
    'Sikorsky S-76', 'Sikorsky S-92',
    // MD
    'MD 902', 'MD 530',
    // Robinson
    'Robinson R44', 'Robinson R66',
    // Classified
    'Airwolf',
  ];

  useEffect(() => {
    fetchContractors();
  }, []);

  const fetchContractors = async () => {
    try {
      setLoading(true);
      const data = await contractorService.getContractors(false); // Get all contractors
      setContractors(data);
    } catch (error: any) {
      console.error('Error fetching contractors:', error);
      toast.error(error.message || 'Failed to load contractors');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    // Company name is required
    if (!formData.companyName.trim()) {
      errors.companyName = 'Company name is required';
    }

    // Validate contact phone if provided
    if (formData.contactPhone && !validatePhoneNumber(formData.contactPhone)) {
      errors.contactPhone = getPhoneValidationError(formData.contactPhone) || 'Invalid phone number';
    }

    // Validate dispatch phone if provided
    if (formData.dispatchPhone && !validatePhoneNumber(formData.dispatchPhone)) {
      errors.dispatchPhone = getPhoneValidationError(formData.dispatchPhone) || 'Invalid phone number';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix validation errors before submitting');
      return;
    }

    try {
      // Prepare data for submission
      const submitData: CreateContractorRequest = {
        companyName: formData.companyName.trim(),
        contactName: formData.contactName.trim() || undefined,
        contactPhone: formData.contactPhone.trim() || undefined,
        dispatchPhone: formData.dispatchPhone.trim() || undefined,
        aircraftType: formData.aircraftType.trim() || undefined,
        active: formData.active
      };

      if (editingContractor) {
        await contractorService.updateContractor(editingContractor.id, submitData);
        toast.success('Contractor updated successfully');
      } else {
        await contractorService.createContractor(submitData);
        toast.success('Contractor created successfully');
      }
      
      setShowModal(false);
      resetForm();
      fetchContractors();
    } catch (error: any) {
      toast.error(error.message || 'Operation failed');
    }
  };

  const handleDelete = async (contractorId: string) => {
    if (!confirm('Are you sure you want to deactivate this contractor?')) return;

    try {
      await contractorService.deleteContractor(contractorId);
      toast.success('Contractor deactivated successfully');
      fetchContractors();
    } catch (error: any) {
      toast.error(error.message || 'Failed to deactivate contractor');
    }
  };

  const resetForm = () => {
    setFormData({
      companyName: '',
      contactName: '',
      contactPhone: '',
      dispatchPhone: '',
      aircraftType: '',
      active: true
    });
    setEditingContractor(null);
    setValidationErrors({});
  };

  const openEditModal = (contractor: AircraftContractor) => {
    setEditingContractor(contractor);
    setFormData({
      companyName: contractor.companyName || '',
      contactName: contractor.contactName || '',
      contactPhone: contractor.contactPhone || '',
      dispatchPhone: contractor.dispatchPhone || '',
      aircraftType: contractor.aircraftType || '',
      active: contractor.active
    });
    setValidationErrors({});
    setShowModal(true);
  };

  const handleQuickAdd = (companyName: string) => {
    setFormData({
      companyName,
      contactName: '',
      contactPhone: '',
      dispatchPhone: '',
      aircraftType: '',
      active: true
    });
    setValidationErrors({});
    setShowModal(true);
  };

  const getStatusIcon = (status: boolean) => {
    return status ? (
      <CheckCircle className="h-5 w-5 text-green-500" />
    ) : (
      <XCircle className="h-5 w-5 text-red-500" />
    );
  };

  const filteredContractors = contractors.filter(c => 
    c.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.contactName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.aircraftType || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-medium text-gray-900">Air Carrier Management</h2>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 flex items-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Contractor
        </button>
      </div>

      {/* Quick Add Section */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="text-sm font-medium text-blue-900 mb-3">Quick Add Common Carriers</h3>
        <div className="flex flex-wrap gap-2">
          {DEFAULT_CARRIERS.map((carrier) => (
            <button
              key={carrier}
              onClick={() => handleQuickAdd(carrier)}
              className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full hover:bg-blue-200 text-sm"
            >
              + {carrier}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search contractors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border rounded-md focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* Contractors Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredContractors.map((contractor) => (
          <div key={contractor.id} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 flex items-center">
                  <Building2 className="h-5 w-5 mr-2 text-gray-400" />
                  {contractor.companyName}
                </h3>
                {contractor.aircraftType && (
                  <p className="text-sm text-gray-600 flex items-center mt-1">
                    <Plane className="h-4 w-4 mr-1" />
                    {contractor.aircraftType}
                  </p>
                )}
              </div>
              <div className="flex items-center">
                {getStatusIcon(contractor.active)}
              </div>
            </div>

            <div className="space-y-2 text-sm">
              {contractor.contactName && (
                <div className="flex items-center">
                  <User className="h-4 w-4 mr-2 text-gray-400" />
                  <span className="text-gray-900">{contractor.contactName}</span>
                </div>
              )}
              
              {contractor.contactPhone && (
                <div className="flex items-center">
                  <Phone className="h-4 w-4 mr-2 text-gray-400" />
                  <span className="text-gray-900">{formatPhoneNumber(contractor.contactPhone)}</span>
                  <span className="ml-1 text-xs text-gray-500">(Contact)</span>
                </div>
              )}
              
              {contractor.dispatchPhone && (
                <div className="flex items-center">
                  <Phone className="h-4 w-4 mr-2 text-red-400" />
                  <span className="text-gray-900 font-medium">{formatPhoneNumber(contractor.dispatchPhone)}</span>
                  <span className="ml-1 text-xs text-red-600 font-medium">(Dispatch)</span>
                </div>
              )}

              <div className="flex items-center text-xs text-gray-500 mt-2">
                <span className={contractor.active ? 'text-green-600' : 'text-red-600'}>
                  {contractor.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t flex justify-end space-x-2">
              <button
                onClick={() => openEditModal(contractor)}
                className="text-indigo-600 hover:text-indigo-900 p-1"
                title="Edit contractor"
              >
                <Edit2 className="h-4 w-4" />
              </button>
              {contractor.active && (
                <button
                  onClick={() => handleDelete(contractor.id)}
                  className="text-red-600 hover:text-red-900 p-1"
                  title="Deactivate contractor"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* No results */}
      {filteredContractors.length === 0 && (
        <div className="text-center py-8">
          <Building2 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No contractors found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'Try adjusting your search criteria.' : 'Get started by adding your first contractor.'}
          </p>
        </div>
      )}

      {/* Contractor Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium mb-4">
              {editingContractor ? 'Edit Contractor' : 'Add New Contractor'}
            </h3>
            
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Company Name */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      validationErrors.companyName ? 'border-red-500' : ''
                    }`}
                    placeholder="Air Medical Services Ltd."
                  />
                  {validationErrors.companyName && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.companyName}</p>
                  )}
                </div>

                {/* Contact Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Contact Name</label>
                  <input
                    type="text"
                    value={formData.contactName}
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                    className="mt-1 block w-full border rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="John Smith"
                  />
                </div>

                {/* Aircraft Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Aircraft Type(s)</label>
                  <input
                    type="text"
                    list="aircraft-type-suggestions"
                    value={formData.aircraftType}
                    onChange={(e) => setFormData({ ...formData, aircraftType: e.target.value })}
                    className="mt-1 block w-full border rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g. Bell 407, AS350"
                  />
                  <datalist id="aircraft-type-suggestions">
                    {aircraftTypes.map(type => (
                      <option key={type} value={type} />
                    ))}
                  </datalist>
                  <p className="mt-1 text-xs text-gray-500">
                    Type freely or pick from suggestions. Use commas for multiple types.
                  </p>
                </div>

                {/* Contact Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Contact Phone</label>
                  <input
                    {...getPhoneInputProps(
                      formData.contactPhone,
                      (value) => setFormData({ ...formData, contactPhone: value })
                    )}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      validationErrors.contactPhone ? 'border-red-500' : ''
                    }`}
                  />
                  {validationErrors.contactPhone && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.contactPhone}</p>
                  )}
                </div>

                {/* Dispatch Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Dispatch Phone
                    <span className="ml-1 text-xs text-red-600">(Emergency Contact)</span>
                  </label>
                  <input
                    {...getPhoneInputProps(
                      formData.dispatchPhone,
                      (value) => setFormData({ ...formData, dispatchPhone: value })
                    )}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:ring-red-500 focus:border-red-500 ${
                      validationErrors.dispatchPhone ? 'border-red-500' : ''
                    }`}
                    placeholder="Emergency dispatch number"
                  />
                  {validationErrors.dispatchPhone && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.dispatchPhone}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Primary number for emergency dispatching and mission coordination
                  </p>
                </div>

                {/* Active Status */}
                <div className="md:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active contractor</span>
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    Only active contractors will appear in mission resource dropdowns
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  {editingContractor ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}